import Link from 'next/link'
import React from 'react'
import { PillarPage, SearchPageCity } from 'src/constants/searchPages'
import ga from 'src/utils/pixels/gtag'
import { getLandingCities } from './CitiesSection'

type ICityLinksProps = {
  closestCity: SearchPageCity | undefined
  pillar?: PillarPage
}

/**
 * The rail's "browse by city" strip — plain text links under the carousel.
 * Deliberately NOT chips: the pillar toggle above filters the rail in place,
 * these navigate away to a city page, and two rows of pills in one header
 * read as two competing filters. One line on mobile (scrolls sideways, every
 * city stays crawlable), wraps on desktop. The visitor's city leads, marked
 * with a blue dot.
 */
function CityLinks({ closestCity, pillar }: ICityLinksProps) {
  const pillarSlug = pillar ?? PillarPage.SelfStorage
  const cities = getLandingCities(closestCity).sort(
    (a, b) => Number(b.city === closestCity) - Number(a.city === closestCity)
  )

  return (
    <ul className="-mx-5 flex [scrollbar-width:none] items-center gap-x-5 overflow-x-auto [mask-image:linear-gradient(to_right,black_85%,transparent)] px-5 [-ms-overflow-style:none] md:mx-0 md:flex-wrap md:gap-x-6 md:overflow-visible md:[mask-image:none] md:px-0 [&::-webkit-scrollbar]:hidden">
      {cities.map(({ city, cityMap }) => {
        const isClosest = city === closestCity
        return (
          <li key={city} className="shrink-0">
            <Link
              href={`/${pillarSlug}/${city}`}
              onClick={() =>
                ga.event('home_city_tile_click', {
                  city,
                  pillar: pillarSlug,
                  is_closest: isClosest
                })
              }
              className={`inline-flex min-h-11 items-center gap-1.5 text-sm whitespace-nowrap underline-offset-[6px] transition-colors duration-200 hover:text-blue-500 hover:underline hover:decoration-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 ${
                isClosest ? 'font-medium text-ink-700' : 'text-ink-200'
              }`}
            >
              {isClosest && (
                <span
                  aria-hidden
                  className="size-1.5 rounded-full bg-blue-500"
                />
              )}
              {cityMap.cityName}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

export default React.memo(CityLinks)
